import Phaser from "phaser";
import { ARENA_PLATFORMS, type ActionInput, type MatchState, type Platform } from "../../game/simulation";
import type { SceneBridge } from "../sceneBridge";
import { toArenaViewModel, type ArenaBubbleView, type ArenaPlayerView, type ArenaViewModel } from "../viewModel";

const TEAM_COLORS = {
  A: { primary: 0x46d7ff, shadow: 0x073e59, glow: 0xa8f1ff },
  B: { primary: 0xff8b6c, shadow: 0x60273a, glow: 0xffd0bd }
} as const;

interface ArenaSceneOptions {
  bridge: SceneBridge;
  readInput: () => ActionInput;
  compact?: boolean;
}

interface PlayerGlyph {
  container: Phaser.GameObjects.Container;
  body: Phaser.GameObjects.Graphics;
  label: Phaser.GameObjects.Text;
  status: Phaser.GameObjects.Text;
  targetX: number;
  targetY: number;
}

interface BubbleGlyph {
  graphic: Phaser.GameObjects.Graphics;
  targetX: number;
  targetY: number;
}

export class ArenaScene extends Phaser.Scene {
  private readonly bridge: SceneBridge;
  private readonly readInput: () => ActionInput;
  private readonly compact: boolean;
  private unsubscribe: (() => void) | null = null;
  private players = new Map<string, PlayerGlyph>();
  private bubbles = new Map<string, BubbleGlyph>();
  private view: ArenaViewModel | null = null;
  private atmosphere: Phaser.GameObjects.Graphics | null = null;
  private platformLayer: Phaser.GameObjects.Graphics | null = null;

  constructor(options: ArenaSceneOptions) {
    super({ key: "foam-arena" });
    this.bridge = options.bridge;
    this.readInput = options.readInput;
    this.compact = options.compact ?? false;
  }

  create(): void {
    this.cameras.main.setBackgroundColor(0x071422);
    this.drawArena();
    this.unsubscribe = this.bridge.onSnapshot((state) => this.receiveSnapshot(state));

    const initial = this.bridge.latestSnapshot();
    if (initial) {
      this.receiveSnapshot(initial);
    }

    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.unsubscribe?.();
      this.unsubscribe = null;
    });
  }

  update(_time: number, delta: number): void {
    this.bridge.emitInput(this.readInput());
    const blend = 1 - Math.pow(0.0008, Math.min(delta, 50) / 1000);

    this.players.forEach((glyph) => {
      glyph.container.x = Phaser.Math.Linear(glyph.container.x, glyph.targetX, blend);
      glyph.container.y = Phaser.Math.Linear(glyph.container.y, glyph.targetY, blend);
    });
    this.bubbles.forEach((glyph) => {
      glyph.graphic.x = Phaser.Math.Linear(glyph.graphic.x, glyph.targetX, blend);
      glyph.graphic.y = Phaser.Math.Linear(glyph.graphic.y, glyph.targetY, blend);
    });

    if (this.atmosphere) {
      this.atmosphere.alpha = 0.38 + Math.sin(this.time.now * 0.0014) * 0.08;
    }
  }

  private drawArena(): void {
    const background = this.add.graphics();
    background.fillGradientStyle(0x0b2035, 0x0b2035, 0x071422, 0x071422, 1);
    background.fillRect(0, 0, 1280, 720);

    background.lineStyle(1, 0x4ce4ee, 0.08);
    for (let x = 0; x <= 1280; x += 64) {
      background.lineBetween(x, 0, x, 720);
    }
    for (let y = 0; y <= 720; y += 64) {
      background.lineBetween(0, y, 1280, y);
    }

    background.fillStyle(0x0b2c43, 0.9);
    background.fillCircle(120, 94, 54);
    background.fillCircle(1160, 94, 54);
    background.lineStyle(3, 0x50e2ef, 0.22);
    background.strokeCircle(120, 94, 54);
    background.strokeCircle(1160, 94, 54);
    background.lineBetween(120, 40, 120, 148);
    background.lineBetween(66, 94, 174, 94);
    background.lineBetween(1160, 40, 1160, 148);
    background.lineBetween(1106, 94, 1214, 94);

    this.platformLayer = this.add.graphics();
    this.drawPlatforms(ARENA_PLATFORMS);

    const center = this.add.graphics();
    center.lineStyle(2, 0xe3fbff, 0.12);
    center.lineBetween(640, 94, 640, 660);
    center.fillStyle(0xe3fbff, 0.08);
    center.fillCircle(640, 646, 22);
    center.lineStyle(2, 0xe3fbff, 0.22);
    center.strokeCircle(640, 646, 22);

    this.atmosphere = this.add.graphics();
    this.atmosphere.fillStyle(0x5fe8f1, 0.45);
    [
      [64, 190, 7], [75, 214, 3], [1210, 168, 5], [1188, 205, 8],
      [512, 88, 4], [772, 120, 6], [934, 616, 4], [342, 611, 5]
    ].forEach(([x, y, radius]) => this.atmosphere?.fillCircle(x ?? 0, y ?? 0, radius ?? 1));
  }

  private receiveSnapshot(state: MatchState): void {
    const view = toArenaViewModel(state);
    this.view = view;
    this.drawPlatforms(view.platforms);
    const playerIds = new Set(view.players.map((player) => player.id));
    const bubbleIds = new Set(view.bubbles.map((bubble) => bubble.id));

    this.players.forEach((glyph, id) => {
      if (!playerIds.has(id)) {
        glyph.container.destroy(true);
        this.players.delete(id);
      }
    });
    this.bubbles.forEach((glyph, id) => {
      if (!bubbleIds.has(id)) {
        glyph.graphic.destroy();
        this.bubbles.delete(id);
      }
    });

    view.players.forEach((player) => this.renderPlayer(player));
    view.bubbles.forEach((bubble) => this.renderBubble(bubble));
  }

  private drawPlatforms(platformData: ReadonlyArray<Readonly<Platform>>): void {
    const platforms = this.platformLayer;
    if (!platforms) return;
    platforms.clear();
    platformData.forEach(({ x, y, width, height, oneWay }) => {
      platforms.fillStyle(oneWay ? 0x102b41 : 0x12334a, 1);
      platforms.fillRoundedRect(x, y, width, height, oneWay ? Math.min(11, height / 2) : 0);
      platforms.fillStyle(0x47d6e8, oneWay ? 0.6 : 0.35);
      platforms.fillRoundedRect(x, y, width, Math.min(5, height), 3);
      platforms.lineStyle(1, 0x8ef4ff, 0.16);
      for (let slot = x + 28; slot < x + width - 16; slot += 48) {
        platforms.lineBetween(slot, y + Math.min(9, height / 2), slot + 14, y + Math.min(9, height / 2));
      }
    });
  }

  private renderPlayer(player: ArenaPlayerView): void {
    let glyph = this.players.get(player.id);
    if (!glyph) {
      const container = this.add.container(player.x, player.y + 24);
      const body = this.add.graphics();
      const label = this.add.text(0, -74, player.name, {
        fontFamily: "Arial, sans-serif",
        fontSize: this.compact ? "18px" : "20px",
        color: "#f1fdff",
        fontStyle: "bold",
        stroke: "#06121e",
        strokeThickness: 5,
        align: "center"
      }).setOrigin(0.5, 1);
      const status = this.add.text(0, -69, "", {
        fontFamily: "Arial, sans-serif",
        fontSize: "13px",
        color: "#b9d8e4",
        stroke: "#06121e",
        strokeThickness: 4
      }).setOrigin(0.5, 0);
      container.add([body, label, status]);
      glyph = { container, body, label, status, targetX: player.x, targetY: player.y + 24 };
      this.players.set(player.id, glyph);
    }

    const palette = TEAM_COLORS[player.team];
    glyph.targetX = player.x;
    glyph.targetY = player.y + 24;
    glyph.label.setText(player.name);
    glyph.status.setText(`${player.kind === "ai" ? "AI" : "HUMAN"}${player.connected ? "" : " · LINK LOST"}`);
    glyph.status.setColor(player.connected ? "#b9d8e4" : "#ffcf75");
    glyph.container.setAlpha(player.connected ? 1 : 0.62);
    glyph.container.setScale(player.facing, 1);
    glyph.label.setScale(player.facing, 1);
    glyph.status.setScale(player.facing, 1);

    const body = glyph.body;
    body.clear();
    body.fillStyle(0x000000, 0.25);
    body.fillEllipse(0, 7, 60, 18);

    if (player.captured) {
      body.fillStyle(palette.primary, 0.12);
      body.fillCircle(0, -34, 55);
      body.lineStyle(5, palette.glow, 0.9);
      body.strokeCircle(0, -34, 55);
      body.lineStyle(2, 0xffffff, 0.55);
      body.strokeCircle(-15, -50, 8);
      body.strokeCircle(19, -21, 5);
    }

    if (player.invulnerable) {
      body.lineStyle(3, 0xffffff, 0.55);
      body.strokeCircle(0, -30, 45 + Math.sin(this.time.now * 0.02) * 3);
    }

    body.fillStyle(palette.shadow, 1);
    body.fillRoundedRect(-28, -61, 56, 65, 24);
    body.fillStyle(palette.primary, 1);
    body.fillRoundedRect(-25, -58, 50, 57, 21);
    body.fillStyle(0xe9fdff, 0.95);
    body.fillCircle(-9, -39, 7);
    body.fillCircle(9, -39, 7);
    body.fillStyle(0x071422, 1);
    body.fillCircle(-7, -39, 3);
    body.fillCircle(11, -39, 3);
    body.fillStyle(palette.glow, 0.72);
    body.fillRoundedRect(-16, -19, 33, 7, 4);
    body.fillStyle(palette.shadow, 1);
    body.fillRoundedRect(23, -37, 23, 15, 7);
    body.fillStyle(palette.primary, 1);
    body.fillCircle(46, -29, 8);
  }

  private renderBubble(bubble: ArenaBubbleView): void {
    let glyph = this.bubbles.get(bubble.id);
    if (!glyph) {
      const graphic = this.add.graphics({ x: bubble.x, y: bubble.y });
      glyph = { graphic, targetX: bubble.x, targetY: bubble.y };
      this.bubbles.set(bubble.id, glyph);
    }
    const palette = TEAM_COLORS[bubble.team];
    glyph.targetX = bubble.x;
    glyph.targetY = bubble.y;
    glyph.graphic.clear();
    glyph.graphic.fillStyle(palette.primary, bubble.trappedPlayerId ? 0.18 : 0.1);
    glyph.graphic.fillCircle(0, 0, bubble.radius);
    glyph.graphic.lineStyle(bubble.trappedPlayerId ? 5 : 3, palette.glow, 0.88);
    glyph.graphic.strokeCircle(0, 0, bubble.radius);
    glyph.graphic.lineStyle(2, 0xffffff, 0.7);
    glyph.graphic.beginPath();
    glyph.graphic.arc(-bubble.radius * 0.22, -bubble.radius * 0.22, bubble.radius * 0.43, 3.55, 5.05);
    glyph.graphic.strokePath();
  }
}
